import {
  usePublishTopicQuestionsMutation,
  useUnpublishTopicQuestionsMutation,
} from "@api/quiz";
import { IconWithTooltip } from "@components/IconWithTooltip";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/16/solid";
import { PublishStatus } from "@onetutor/shared/type";
import { useRequiredParams } from "@utils/params";
import { useTranslation } from "react-i18next";

interface PublishStatusIconProps {
  topicId: string;
  publishStatus?: PublishStatus;
}

export function PublishStatusIcon({
  topicId,
  publishStatus,
}: PublishStatusIconProps) {
  const { t } = useTranslation(["quizManagementPage"]);
  const { courseId } = useRequiredParams(["courseId"]);

  const [publishTopicQuestions, { isLoading: isLoadingPublish }] =
    usePublishTopicQuestionsMutation();
  const [unpublishTopicQuestions, { isLoading: isLoadingUnpublish }] =
    useUnpublishTopicQuestionsMutation();

  const handlePublishToggle = async () => {
    const newPublishState = publishStatus !== "all";

    if (newPublishState) {
      await publishTopicQuestions({ courseId, topicId });
    } else {
      await unpublishTopicQuestions({ courseId, topicId });
    }
  };

  if (isLoadingPublish || isLoadingUnpublish) {
    return (
      <IconWithTooltip
        icon={<span className="loading loading-spinner h-3 w-3" />}
        tooltip={t("tooltipPublishQuestions")}
        disabled={true}
        tooltipPosition="left"
      />
    );
  }

  if (publishStatus === "all") {
    return (
      <IconWithTooltip
        icon={<EyeIcon className="h-3 w-3 text-success" />}
        tooltip={t("tooltipUnpublishAllQuestions")}
        handler={handlePublishToggle}
        tooltipPosition="left"
      />
    );
  }

  if (publishStatus === "some") {
    return (
      <IconWithTooltip
        icon={<EyeSlashIcon className="h-3 w-3 text-warning" />}
        tooltip={t("tooltipPublishAllQuestions")}
        handler={handlePublishToggle}
        tooltipPosition="left"
      />
    );
  }

  return (
    <IconWithTooltip
      icon={<EyeSlashIcon className="h-3 w-3 text-base-300" />}
      tooltip={t("tooltipPublishAllQuestions")}
      disabled={false}
      handler={handlePublishToggle}
      tooltipPosition="left"
    />
  );
}
